// Client KB: per-client markdown library helpers (no UI)

import { supabase } from './supabase';
import type {
  ClientKbFile,
  ClientKbKind,
  ClientKbSearchHit,
  ClientKbStatus,
} from './supabase.types';

export type {
  ClientKbFile,
  ClientKbKind,
  ClientKbSearchHit,
  ClientKbStatus,
};

export type CreateKbFileInput = {
  client_id: string;
  title: string;
  as_of: string;
  kind: ClientKbKind;
  body?: string;
  status?: ClientKbStatus;
  added_by?: string | null;
  superseded_by?: string | null;
};

export type UpdateKbFileInput = {
  title?: string;
  body?: string;
  as_of?: string;
  kind?: ClientKbKind;
  status?: ClientKbStatus;
  superseded_by?: string | null;
};

const SEARCH_COLUMNS =
  'id, client_id, title, as_of, kind, status, added_by, created_at' as const;

/** Escape `%`, `_`, and `\` so ILIKE is a literal keyword match. */
function escapeIlike(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

/** Strip PostgREST `.or()` metacharacters so a user query cannot split filters. */
function sanitizeOrValue(value: string): string {
  return value.replace(/[,()]/g, ' ').trim();
}

export async function listKbFiles(clientId: string): Promise<ClientKbFile[]> {
  const { data, error } = await supabase
    .from('client_kb_files')
    .select('*')
    .eq('client_id', clientId)
    .order('as_of', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as ClientKbFile[];
}

export async function getKbFile(id: string): Promise<ClientKbFile | null> {
  const { data, error } = await supabase
    .from('client_kb_files')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return data as ClientKbFile | null;
}

export async function createKbFile(input: CreateKbFileInput): Promise<ClientKbFile> {
  const { data, error } = await supabase
    .from('client_kb_files')
    .insert({
      client_id: input.client_id,
      title: input.title,
      as_of: input.as_of,
      kind: input.kind,
      body: input.body ?? '',
      status: input.status ?? 'current',
      added_by: input.added_by ?? null,
      superseded_by: input.superseded_by ?? null,
    })
    .select('*')
    .single();

  if (error) throw error;
  return data as ClientKbFile;
}

export async function updateKbFile(
  id: string,
  patch: UpdateKbFileInput
): Promise<ClientKbFile> {
  const { data, error } = await supabase
    .from('client_kb_files')
    .update({
      ...patch,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw error;
  return data as ClientKbFile;
}

export async function deleteKbFile(id: string): Promise<void> {
  const { error } = await supabase.from('client_kb_files').delete().eq('id', id);

  if (error) throw error;
}

/**
 * The knowledge file a client must have before an article can be deployed to
 * their CMS. It is the playbook the deploy agent follows — which schema, which
 * model, how images and SEO get in, which build hook to fire. Without it the
 * agent would be guessing at a live client site, so Deploy stays locked.
 */
export const ARTICLE_DEPLOYMENT_TITLE = 'Article Deployment';

/**
 * The client's current Article Deployment file, or null. Title match is exact
 * but case-insensitive, and `%`/`_` are escaped so the constant can never be
 * read as an ILIKE pattern. Superseded copies are ignored — a stale playbook
 * pointed at the wrong schema is worse than none.
 */
export async function fetchArticleDeploymentKb(
  clientId: string
): Promise<ClientKbFile | null> {
  const { data, error } = await supabase
    .from('client_kb_files')
    .select('*')
    .eq('client_id', clientId)
    .eq('status', 'current')
    .ilike('title', escapeIlike(ARTICLE_DEPLOYMENT_TITLE))
    .order('as_of', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(1);

  if (error) throw error;
  return (data?.[0] as ClientKbFile | undefined) ?? null;
}

export async function searchKbFiles(
  clientId: string,
  query: string
): Promise<ClientKbSearchHit[]> {
  const keyword = sanitizeOrValue(query);
  if (!keyword) return [];

  const pattern = `%${escapeIlike(keyword)}%`;
  const { data, error } = await supabase
    .from('client_kb_files')
    .select(SEARCH_COLUMNS)
    .eq('client_id', clientId)
    .or(`title.ilike.${pattern},body.ilike.${pattern}`)
    .order('as_of', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as ClientKbSearchHit[];
}
